/* ═══════════════════════════════════════════════════════════════
   TimeService — Hour Forecasts, Hour Entries, Phases, Deliverables,
   Timesheets (weekly submit/approve) via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type {
  HourForecast,
  HourEntry,
  ProjectPhase,
  Deliverable,
  Timesheet,
  TimesheetAuditEntry,
} from '../../shared/types';
import { getCurrentFirmId } from './firmContext';
import * as cache from './cloudCache';
import * as cloudApi from './apiClient';
import { log as logActivity } from './activityLogger';

function toCamel(row: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(row)) {
    result[k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v;
  }
  return result;
}

export const TimeService = {
  // ─── Phases ───
  listPhases: async (projectId: string): Promise<ProjectPhase[]> => {
    return cache.query<ProjectPhase>('project_phases', { projectId });
  },
  savePhase: async (phase: ProjectPhase): Promise<ProjectPhase> => {
    return cache.save('project_phases', phase);
  },
  deletePhase: async (id: string): Promise<void> => cache.remove('project_phases', id),

  /**
   * The ONE way to read a project's phases: the admin-center "Phases" list
   * (default_phases — user-created, user-ordered) IS the source of truth.
   * The project's rows are synced to it — admin order enforced, missing
   * phases created, project-specific extras kept (appended, never deleted).
   * NEVER writes default_phases. Every phase display goes through this so
   * the admin list and every widget always agree.
   */
  syncPhasesWithDefaults: async (projectId: string): Promise<ProjectPhase[]> => {
    const uniqueByName = (list: ProjectPhase[]): ProjectPhase[] => {
      const seen = new Set<string>();
      return list.filter((p) => (seen.has(p.name) ? false : (seen.add(p.name), true)));
    };
    const rawDefaults = await cache.list<{ id: string; name: string; sortOrder?: number }>(
      'default_phases'
    );
    const seenDefault = new Set<string>();
    const defaults = rawDefaults
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((d) => (seenDefault.has(d.name) ? false : (seenDefault.add(d.name), true)));
    const existing = await cache.query<ProjectPhase>('project_phases', { projectId });

    if (defaults.length === 0) {
      return uniqueByName(existing).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    const synced: ProjectPhase[] = [];
    for (let i = 0; i < defaults.length; i++) {
      const fp = defaults[i];
      const match = existing.find((e) => e.name === fp.name);
      if (match) {
        synced.push(
          match.sortOrder !== i
            ? await cache.save('project_phases', { ...match, sortOrder: i })
            : match
        );
      } else {
        synced.push(
          await cache.save('project_phases', {
            id: '',
            projectId,
            name: fp.name,
            status: 'pending',
            sortOrder: i,
            createdAt: '',
            updatedAt: '',
          } as ProjectPhase)
        );
      }
    }
    // Project-specific extras (not in firm defaults) stay, after the defaults.
    const usedNames = new Set(defaults.map((f) => f.name));
    for (const e of existing) {
      if (!usedNames.has(e.name)) {
        usedNames.add(e.name);
        synced.push(e);
      }
    }
    return synced.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  },

  // ─── Deliverables ───
  listDeliverables: async (projectId: string): Promise<Deliverable[]> => {
    return cache.query<Deliverable>('deliverables', { projectId });
  },
  saveDeliverable: async (d: Deliverable): Promise<Deliverable> => {
    return cache.save('deliverables', d);
  },
  deleteDeliverable: async (id: string): Promise<void> => cache.remove('deliverables', id),

  // ─── Forecasts ───
  listForecasts: async (projectId?: string, weekStart?: string): Promise<HourForecast[]> => {
    const filters: Record<string, any> = {};
    if (projectId) filters.projectId = projectId;
    if (weekStart) filters.weekStartDate = weekStart;
    if (Object.keys(filters).length > 0) {
      return cache.query<HourForecast>('hour_forecasts', filters);
    }
    return cache.list<HourForecast>('hour_forecasts');
  },
  saveForecast: async (f: HourForecast): Promise<HourForecast> => {
    const saved = await cache.save('hour_forecasts', f);
    logActivity({
      action: 'forecast.save',
      entityType: 'hour_forecast',
      entityId: saved.id,
      metadata: { hours: saved.hours, projectId: saved.projectId, dayDate: saved.dayDate },
    });
    return saved;
  },
  deleteForecast: async (id: string): Promise<void> => cache.remove('hour_forecasts', id),

  /** Pull forecasts in a date range, optionally narrowed to a set of members.
   *  Server-side filter on day_date so the wire payload stays small even for
   *  large firms (one weekly view = ~7 days × ~50 people × ~3 projects).
   *
   *  Routed through cache.queryAdvanced (30s TTL, table-level invalidation
   *  on writes) so the dashboard's many overlapping range queries collapse
   *  to a single network call per unique range. */
  listForecastsForRange: async (
    startDate: string,
    endDate: string,
    memberIds?: string[]
  ): Promise<HourForecast[]> => {
    const filters: { col: string; op: string; val: any }[] = [
      { col: 'day_date', op: '>=', val: startDate },
      { col: 'day_date', op: '<=', val: endDate },
    ];
    if (memberIds && memberIds.length > 0) {
      filters.push({ col: 'team_member_id', op: 'IN', val: memberIds });
    }
    return cache.queryAdvanced<HourForecast>('hour_forecasts', filters);
  },

  /** Upsert a single day-cell via the server's atomic forecast_upsert_cell
   *  endpoint. Single round-trip, idempotent, dedupe-safe (backed by partial
   *  unique index uq_hour_forecasts_manual_cell). hours <= 0 deletes the cell.
   *  Phase is project-level (migration 017) — the DB trigger stamps it from
   *  projects.current_phase_id, so this method intentionally takes no phase
   *  argument. Vacation rows are NEVER touched here; they're managed by
   *  syncVacationToForecasts and live outside the manual partial index. */
  setForecastDayCell: async (args: {
    teamMemberId: string;
    projectId: string;
    dayDate: string;
    hours: number;
    notes?: string;
  }): Promise<void> => {
    const { teamMemberId, projectId, dayDate, hours, notes } = args;
    await cloudApi.forecastUpsertCell({
      teamMemberId,
      projectId: projectId || null,
      dayDate,
      hours,
      notes: notes ?? null,
    });
    cache.invalidate('hour_forecasts');
  },

  /** Sync forecasts for an approved vacation request — creates locked
   *  source_kind='vacation' rows on every workday in the range, with
   *  hours = vacationRequest.hoursPerDay.  Called from the vacation
   *  approval flow; safe to call repeatedly (idempotent on day_date).
   *  Action 'remove' deletes all vacation-source rows for the request. */
  syncVacationToForecasts: async (
    vacationRequestId: string,
    action: 'create' | 'remove',
    payload?: {
      teamMemberId: string;
      startDate: string;
      endDate: string;
      hoursPerDay: number;
      // Default vacation project — when undefined, the row is project_id=null
      // forecast (visible on the grid as a personal block, not project-bound).
      projectId?: string;
    }
  ): Promise<void> => {
    const firmId = getCurrentFirmId();

    // Always start by removing any existing vacation rows for this request
    // — keeps the sync idempotent and handles range edits cleanly.
    const existing = await cloudApi.dbQuery('hour_forecasts', firmId, [
      { col: 'source_kind', op: '=', val: 'vacation' },
      { col: 'source_id', op: '=', val: vacationRequestId },
    ]);
    for (const row of existing || []) {
      await cache.remove('hour_forecasts', row.id);
    }

    if (action === 'remove' || !payload) return;

    const { teamMemberId, startDate, endDate, hoursPerDay, projectId } = payload;
    const days = enumerateWorkdays(startDate, endDate);
    for (const day of days) {
      const f: HourForecast = {
        id: crypto.randomUUID(),
        projectId: projectId || '',
        teamMemberId,
        weekStartDate: mondayOf(day),
        dayDate: day,
        hours: hoursPerDay,
        sourceKind: 'vacation',
        sourceId: vacationRequestId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await cache.save('hour_forecasts', f);
    }
  },

  // ─── Hour Entries ───
  /** List hour entries with optional project / date / member filters.
   *  Routed through cache.queryAdvanced so the project dashboard's many
   *  widgets (Hours, Cost, Profitability, BurnRate, Variance, RecentActivity,
   *  PhaseHealth) all sharing the same `listEntries(projectId)` call collapse
   *  to a single network round-trip. */
  listEntries: async (
    projectId?: string,
    startDate?: string,
    endDate?: string,
    memberId?: string
  ): Promise<HourEntry[]> => {
    const filters: { col: string; op: string; val: any }[] = [];
    if (projectId) filters.push({ col: 'project_id', op: '=', val: projectId });
    if (memberId) filters.push({ col: 'team_member_id', op: '=', val: memberId });
    if (startDate) filters.push({ col: 'date', op: '>=', val: startDate });
    if (endDate) filters.push({ col: 'date', op: '<=', val: endDate });
    return cache.queryAdvanced<HourEntry>('hour_entries', filters);
  },
  saveEntry: async (e: HourEntry): Promise<HourEntry> => {
    const saved = await cache.save('hour_entries', e);
    logActivity({
      action: 'time.save',
      entityType: 'hour_entry',
      entityId: saved.id,
      metadata: { hours: saved.hours, projectId: saved.projectId, date: saved.date },
    });
    return saved;
  },
  deleteEntry: async (id: string): Promise<void> => cache.remove('hour_entries', id),

  // ─── Timesheets (weekly submit/approve) ───
  listTimesheets: async (memberId?: string, status?: string): Promise<Timesheet[]> => {
    const filters: Record<string, any> = {};
    if (memberId) filters.teamMemberId = memberId;
    if (status) filters.status = status;
    if (Object.keys(filters).length > 0) {
      return cache.query<Timesheet>('timesheets', filters);
    }
    return cache.list<Timesheet>('timesheets');
  },

  getOrCreateTimesheet: async (memberId: string, weekStart: string): Promise<Timesheet> => {
    if (!memberId) throw new Error('No team member selected');
    const firmId = getCurrentFirmId();

    // Check if timesheet already exists
    const existing = await cloudApi.dbList('timesheets', firmId, {
      team_member_id: memberId,
      week_start: weekStart,
    });

    if (existing && existing.length > 0) {
      return toCamel(existing[0]) as Timesheet;
    }

    // Create new timesheet
    const now = new Date().toISOString();
    const newTs: Timesheet = {
      id: crypto.randomUUID(),
      firmId,
      teamMemberId: memberId,
      weekStart,
      status: 'draft',
      createdAt: now,
      updatedAt: now,
    };

    await cache.save('timesheets', newTs);

    // Create audit entry for creation
    const auditEntry: TimesheetAuditEntry = {
      id: crypto.randomUUID(),
      firmId,
      timesheetId: newTs.id,
      action: 'created',
      performedBy: memberId,
      createdAt: now,
    };
    await cache.save('timesheet_audit_log', auditEntry);

    return newTs;
  },

  getTimesheetByWeek: async (memberId: string, weekStart: string): Promise<Timesheet | null> => {
    const firmId = getCurrentFirmId();
    const data = await cloudApi.dbList('timesheets', firmId, {
      team_member_id: memberId,
      week_start: weekStart,
    });
    if (!data || data.length === 0) return null;
    return toCamel(data[0]) as Timesheet;
  },

  updateTimesheetStatus: async (
    id: string,
    status: Timesheet['status'],
    performedBy: string
  ): Promise<Timesheet> => {
    const firmId = getCurrentFirmId();
    const now = new Date().toISOString();

    const updates: Record<string, any> = { status, updated_at: now };
    if (status === 'submitted') updates.submitted_at = now;
    if (status === 'approved') {
      updates.approved_by = performedBy;
      updates.approved_at = now;
    }
    if (status === 'locked') {
      updates.locked_by = performedBy;
      updates.locked_at = now;
    }

    const results = await cloudApi.dbUpdate('timesheets', updates, [
      { col: 'id', op: '=', val: id },
      { col: 'firm_id', op: '=', val: firmId },
    ]);
    if (!results || results.length === 0) throw new Error('Failed to update timesheet status');

    // Audit
    const auditEntry: TimesheetAuditEntry = {
      id: crypto.randomUUID(),
      firmId,
      timesheetId: id,
      action: status as TimesheetAuditEntry['action'],
      performedBy,
      createdAt: now,
    };
    await cache.save('timesheet_audit_log', auditEntry);

    cache.invalidate('timesheets');
    return toCamel(results[0]) as Timesheet;
  },

  deleteTimesheet: async (id: string): Promise<void> => cache.remove('timesheets', id),

  // ─── Audit Log ───
  addAuditEntry: async (
    _timesheetId: string,
    _action: TimesheetAuditEntry['action'],
    _performedBy: string,
    _details?: string
  ): Promise<void> => {
    // No-op — audit entries are created by updateTimesheetStatus / getOrCreateTimesheet
  },

  listAuditEntries: async (timesheetId: string): Promise<TimesheetAuditEntry[]> => {
    return cache.query<TimesheetAuditEntry>('timesheet_audit_log', { timesheetId });
  },
};

// ─── Date helpers ────────────────────────────────────────────────────
// Forecast/timesheet math uses Monday-anchored weeks.  These helpers
// produce ISO date strings (YYYY-MM-DD) without TZ shenanigans — every
// place that writes day_date / week_start_date should round-trip through
// these so the wire format stays consistent.

/** Returns the Monday-of-week ISO date for any input date string. */
export function mondayOf(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay(); // 0=Sun .. 6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift so Monday=0
  dt.setUTCDate(dt.getUTCDate() + diff);
  return dt.toISOString().slice(0, 10);
}

/** All workdays (Mon–Fri) between start and end, inclusive, as ISO strings. */
export function enumerateWorkdays(startDate: string, endDate: string): string[] {
  const out: string[] = [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const start = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/** All days (including weekends) between start and end, inclusive. */
export function enumerateDays(startDate: string, endDate: string): string[] {
  const out: string[] = [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const start = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));
  const cursor = new Date(start);
  while (cursor <= end) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/** Add N days to an ISO date string. */
export function addDays(isoDate: string, n: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
